
import React from 'react'
import PropTypes from 'prop-types'
import FontAwesome from 'react-fontawesome'
import NotificationListItem from './NotificationListItem'
import './styles.css'
import apiServerApi from '../../redux/sagas/apiServerApi'
import { blockingFetchService } from '../../lib/blockingFetch'
import { connect } from 'react-redux'
import {
  markAllAsRead,
  deleteAllNotifications,
  selectors as notificationsSelectors,
  updateNotification,
  removeNotification,
  fetchUnreadCount
} from '../../redux/notifications'
import { DEFAULT_NAV_PAGE, DEFAULT_NAV_SIZE_50, scrollToTop } from '../../lib/utils'
import { checkAndRoute } from '../../lib/queryStringBuilder'
import { Alert } from 'react-bootstrap'
import AlertModal from '../fragments/AlertModal'

export const NotificationType = {
  SUCCESS: 'SUCCESS',
  ERROR: 'ERROR',
  WARNING: 'WARNING',
  INFO: 'INFO',
}

const AUTO_READ_DELAY = 5000

class NotificationList extends React.Component {

  static contextTypes = {
    router: PropTypes.object,
  }

  constructor(props) {
    super(props)

    this.state = {
      items: [],
      filter: 'all',
      page: Number((props.location && props.location.query && props.location.query.page) || DEFAULT_NAV_PAGE),
      size: Number((props.location && props.location.query && props.location.query.size) || DEFAULT_NAV_SIZE_50),
      totalElements: 0,
      hasNext: false,
      selectedIds: [],
      running: false,
      loading: false,
      error: null,
      deleteAllClicked: false,
      deleteSelectedClicked: false,
      readAllClicked: false,
      readSelectedClicked: false,
      toolbarElevated: false,
    }

    this.autoReadTimer = null
    this.sentinelRef = null
  
    this.setSentinelRef = elt => {
      this.sentinelRef = elt
    }
    this.observer = null
  }

  componentDidMount() {
    this.readQueryString()
    this.configureStickyNavbar()
    this.props.fetchUnreadCount(true)
  }

  componentDidUpdate(prevProps) {
    if (prevProps.location.search !== this.props.location.search) {
      this.readQueryString()
    }
  }

  componentWillUnmount() {
    clearTimeout(this.autoReadTimer)
    if (this.observer) this.observer.disconnect()
  }

  // IntersectionObserver watches the sentinel placed above the toolbar.
  // When the sentinel scrolls out of view (rootMargin: -54px), the toolbar is visually sticky.
  configureStickyNavbar = () => {

    try {
      this.observer = new IntersectionObserver(entries => {
        const entry = entries[0],
        elevated = !entry.isIntersecting
        if (elevated !== this.state.toolbarElevated) {
          this.setState({ toolbarElevated: elevated })
        }
      }, { root: null, threshold: 0, rootMargin: `-54px 0px 0px 0px` })

      if (this.sentinelRef) this.observer.observe(this.sentinelRef)
    }
    catch (e) {
      this.observer = null
    }
  }

  getCurrentParams = () => {
    const query = (this.props.location && this.props.location.query) || {},
    page = Number(query.page || DEFAULT_NAV_PAGE),
    size = Number(query.size || DEFAULT_NAV_SIZE_50)
    return { page, size }
  }

  readQueryString = () => {
    const { page, size } = this.getCurrentParams()
    this.loadData(page, size)
  }

  scheduleAutoReadForVisibleItems = notificationIds => {
    this.autoReadTimer = setTimeout(() => {
      this.doMarkAllRead(notificationIds)
    }, AUTO_READ_DELAY)
  }

  loadData = async (page, size) => {

    this.setState({ loading: true, error: null })

    blockingFetchService(
      accessToken => apiServerApi.getNotifications(accessToken, Math.max(0, page - 1), size),
      (payload = {}) => {

        const items = payload.content || []
        this.setState({
          items,
          totalElements: Number(payload.totalElements),
          page: Number(page),
          size: Number(size),
          hasNext: Boolean(payload.hasNext),
          selectedIds: [],
        })

        // const notReadIds = items.filter(item => !item.read).map(item => item.id)
        // this.scheduleAutoReadForVisibleItems(notReadIds)
      },
      error => {
        console.error('Unable to load notifications', error)
        this.setState({ error: error.text })
      },
      _ => this.setState({ loading: false })
    )
  }

  toggleSelect = id => {
    this.setState(prevState => ({
      selectedIds: prevState.selectedIds.includes(id)
        ? prevState.selectedIds.filter((entryId) => entryId !== id)
        : [...prevState.selectedIds, id],
    }))
  }

  doMarkAllRead = notificationIds => {

    if (!notificationIds.length) return
    this.setState({ running: true, error: null })

    blockingFetchService(
      accessToken => apiServerApi.markAllNotificationsRead(accessToken, notificationIds),
      () => {
        this.setState((prevState) => ({
          items: prevState.items.map((item) => ({
             ...item,
             read: notificationIds.includes(item.id) ? true : item.read
          })),
        }))

        notificationIds.forEach(nId => this.props.updateNotification(nId, { read: true }))
        this.props.fetchUnreadCount(true)
      },
      error => {
        console.error('Unable to mark all notifications as read', error)
        this.setState({ error: error.text })
      },
      _ => {
        this.setState({ running: false })
      }
    )
  }

  doDeleteAll = notificationIds => {

    if (!notificationIds.length) return
    this.setState({ running: true, error: null })

    blockingFetchService(
      accessToken => apiServerApi.deleteAllNotifications(accessToken, notificationIds),
      () => {

        const remainingItems = Math.max(0, this.state.items.length - notificationIds.length)
        this.setState(prevState => ({
          items: prevState.items.filter(item => !notificationIds.includes(item.id)),
          selectedIds: prevState.selectedIds.filter(id => !notificationIds.includes(id)),
          totalElements: Math.max(0, (prevState.totalElements - notificationIds.length)),
        }))

        notificationIds.forEach(nId => this.props.removeNotification(nId))
        this.props.fetchUnreadCount(true)

        if (remainingItems < 6) {
          const { page, size } = this.getCurrentParams()

          if (page === 1) {
            this.loadData(page, size)
          }
          else {
            const nextQuery = { 
              ...this.props.location.query,
              page: page - 1
            }
            if ((page - 1) <= 1) {
              delete nextQuery['page']
            }

            if (this.context && this.context.router) {
              this.context.router.push({
                pathname: this.props.location.pathname,
                query: nextQuery,
              })
            }
            else { 
              this.setState({ error: "Unable to go to the first page" })
            }
          }
        }
      },
      error => {
        console.error('Unable to delete all notifications', error)
        this.setState({ error: error.text })
      },
      _ => this.setState({ running: false })
    )
  }

  markSelectedRead = () => {
    this.doMarkAllRead(this.state.selectedIds)
  }

  deleteSelected = () => {
    this.doDeleteAll(this.state.selectedIds)
  }

  markAllRead = () => {
    this.setState({ running: true, error: null })
    blockingFetchService(
      accessToken => apiServerApi.markAllNotificationsRead(accessToken),
      () => {
        this.setState(prevState => ({ items: prevState.items.map(i => ({ ...i, read: true })) }))
        this.props.markAllAsRead()
      },
      error => this.setState({ error: error && error.text }),
      _ => this.setState({ running: false })
    )
  }

  deleteAll = () => {
    this.setState({ running: true, error: null })
    blockingFetchService(
      accessToken => apiServerApi.deleteAllNotifications(accessToken),
      () => {
        this.setState({ items: [], selectedIds: [], totalElements: 0 })
        this.props.deleteAllNotifications()
      },
      error => this.setState({ error: error && error.text }),
      _ => this.setState({ running: false })
    )
  }

  setFilter = (filter) => {
    this.setState({ filter })
  }

  onNextPage = () => {

    if (!this.state.hasNext) return
    const { page: currentPage } = this.getCurrentParams()

    checkAndRoute('page', currentPage + 1, (currentPage + 1) !== DEFAULT_NAV_PAGE, false, this.context, this.props.location)
    scrollToTop()
  }

  onPreviousPage = () => {
    const { page: currentPage } = this.getCurrentParams()
    if (currentPage <= DEFAULT_NAV_PAGE) return

    checkAndRoute('page', currentPage - 1, (currentPage - 1) !== DEFAULT_NAV_PAGE, false, this.context, this.props.location)
    scrollToTop()
  }

  getFilteredItems = () => {
    const { items, filter } = this.state

    if (filter === 'unread') return items.filter(item => !item.read)
    if (filter === 'read') return items.filter(item => item.read)
    return items
  }

  toggleSelectAllVisible = () => {

    const visibleIds = this.getFilteredItems().map(item => item.id),
    { selectedIds } = this.state,
    allVisibleSelected = visibleIds.length > 0 && visibleIds.every(id => selectedIds.includes(id))

    this.setState({
      selectedIds: allVisibleSelected
        ? selectedIds.filter(id => !visibleIds.includes(id))
        : [...new Set([...selectedIds, ...visibleIds])],
    })
  }
  
  renderPagination = () => {

    const { page, running, hasNext, totalElements, size } = this.state
    return (
      <div className="notification-pagination">
        <button type="button" onClick={ this.onPreviousPage }
          disabled={ (page <= DEFAULT_NAV_PAGE) || running }>
          <FontAwesome name="chevron-left" /> Prev
        </button>
        <span>
          Page { page } of { Math.max(1, Math.ceil(totalElements / size)) }
        </span>
        <button type="button" onClick={ this.onNextPage }
          disabled={ !hasNext || running }>
          Next <FontAwesome name="chevron-right" />
        </button>
      </div>
    )
  }

  render() {

    const { filter, selectedIds, loading, running, error,
    deleteAllClicked, deleteSelectedClicked, readAllClicked, readSelectedClicked, totalElements } = this.state,
    unreadCount = this.props.unreadCount || 0,

    filtered = this.getFilteredItems(),
    visibleIds = filtered.map((item) => item.id),
    allVisibleSelected = visibleIds.length > 0 && visibleIds.every(id => selectedIds.includes(id))

    return (
      <div className="notification-list-page">
        <main className="notification-list-main">
          <div className="notification-header-row">
            <div>
              <h1 className="notification-list-title">Notification Center</h1>
              <p className="notification-list-subtitle">
                Keep track of important updates and alerts.
              </p>
            </div>

            <div className="notification-toolbar-actions">
              <button type="button" className="list-action-button"
                onClick={() => this.setState({ readAllClicked: true })}
                disabled={ running || !totalElements }>
                <FontAwesome name="check-circle" /> Mark all as read
              </button>
              <button type="button" className="list-action-button danger"
                onClick={() => this.setState({ deleteAllClicked: true })}
                disabled={ running || !totalElements }>
                <FontAwesome name="trash" /> Delete all
              </button>
            </div>
          </div>

          <div className="notification-stats-grid">
            <div className="notification-stat-card">
              <span>Total</span>
              <strong>{ totalElements }</strong>
            </div>
            <div className="notification-stat-card">
              <span>Unread</span>
              <strong>{ unreadCount }</strong>
            </div>
            <div className="notification-stat-card">
              <span>Read</span>
              <strong>{ Math.max(0, totalElements - unreadCount) }</strong>
            </div>
            <div className="notification-stat-card">
              <span>Selected</span>
              <strong>{ selectedIds.length }</strong>
            </div>
          </div>

          <div ref={ this.setSentinelRef } style={{ height: 1 }} aria-hidden="true" />

          <div className={`notification-list-toolbar ${this.state.toolbarElevated ? 'elevated' : ''}`}>
            <div className="notification-filter-bar" role="tablist" aria-label="Notification filters">
              {['all', 'unread', 'read'].map((option) => (
                <button
                  key={ option }
                  type="button"
                  className={`notification-filter-btn ${filter === option ? 'active' : ''}`}
                  onClick={() => this.setFilter(option)}
                >
                  { option === 'all' ? 'All' : option === 'unread' ? 'Unread' : 'Read' }
                  { option === 'unread' && unreadCount > 0 && (
                    <span className="filter-badge">{ unreadCount }</span>
                  )}
                </button>
              ))}
            </div>

            <div className="bulk-actions">
              <label className="select-all-checkbox">
                <input
                  type="checkbox"
                  checked={ allVisibleSelected }
                  onChange={ this.toggleSelectAllVisible }
                  className='m-0'
                  aria-label="Select all visible notifications"
                />
                Select page
              </label>

              <button type="button" className="list-action-button small"
                onClick={() => this.setState({ readSelectedClicked: true })}
                disabled={ running || !selectedIds.length }>
                Mark selected read
              </button>
              <button type="button" className="list-action-button danger small"
                onClick={() => this.setState({ deleteSelectedClicked: true })}
                disabled={ running || !selectedIds.length }>
                Delete selected
              </button>
            </div>
          </div>

          { error && <Alert bsStyle="danger">{ error }</Alert> }

          {loading && (
            <div className="notification-empty-state">
              <div className="spinner" />
              <p>Loading notifications...</p>
            </div>
          )}

          {!loading && !filtered.length ? (
            <div className="notification-empty-state">
              <FontAwesome name="bell-slash" />
              <h2>No notifications</h2>
              <p>{ filter === 'all'
                ? "Take a break, we'll let you know when something new arrives."
                : "There are no notifications matching the current filter."
              }</p>
            </div>
          ) : (
            <div>
              <div className="notification-list-items">
                {filtered.map(notification => (
                  <NotificationListItem
                    key={ notification.id }
                    notification={ notification }
                    selected={ selectedIds.includes(notification.id) }
                    onToggleSelect={ this.toggleSelect }
                    onMarkRead={id => this.doMarkAllRead([id])}
                    onDelete={id => this.doDeleteAll([id])}
                    running={ running }
                  />
                ))}
              </div>

              { this.renderPagination() }
            </div>
          )}
        </main>

        <AlertModal
          show={ readAllClicked }
          title="Confirm update"
          body={ <h3>This action will mark all notifications as read</h3> }
          bsStyle="danger"
          close={() => this.setState({ readAllClicked: false })}
          confirm={() => {
            this.setState({ readAllClicked: false })
            this.markAllRead()
          }}
          proceedText="Proceed"
          disabled={ running }
        />

        <AlertModal
          show={ readSelectedClicked }
          title="Confirm update"
          body={ <h3>This action will mark all selected notifications as read</h3> }
          bsStyle="danger"
          close={() => this.setState({ readSelectedClicked: false })}
          confirm={() => {
            this.setState({ readSelectedClicked: false })
            this.markSelectedRead() 
          }}
          proceedText="Proceed"
          disabled={ running }
        />

        <AlertModal
          show={ deleteAllClicked }
          title="Confirm deletion"
          body={ <h3>This action will delete all notifications</h3> }
          bsStyle="danger"
          close={() => this.setState({ deleteAllClicked: false })}
          confirm={() => {
            this.setState({ deleteAllClicked: false })
            this.deleteAll()
          }}
          proceedText="Delete all"
          disabled={ running }
        />

        <AlertModal
          show={ deleteSelectedClicked }
          title="Confirm deletion"
          body={ <h3>This action will delete all selected notifications</h3> }
          bsStyle="danger"
          close={() => this.setState({ deleteSelectedClicked: false })}
          confirm={() => {
            this.setState({ deleteSelectedClicked: false })
            this.deleteSelected() 
          }}
          proceedText="Proceed"
          disabled={ running }
        />
      </div>
    )
  }
}

const mapDispatch = {
  markAllAsRead,
  deleteAllNotifications,
  updateNotification,
  removeNotification,
  fetchUnreadCount,
}

const mapState = state => ({
  unreadCount: notificationsSelectors.unreadCount(state),
})

export const NotificationListContainer = connect(mapState, mapDispatch)(NotificationList)

export default NotificationListContainer



// WEBPACK FOOTER //
// ./src/components/Notifications/List.js